import React, { useState, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { useFlowThresholds } from '../hooks/useFlowThresholds';
import { useDeviceContext, FlowDegradationTimeline } from '../contexts/DeviceContext';
import { FLOW_STALENESS_THRESHOLD_MS } from '../types';
import { shortDeviceName } from '../utils/deviceNameUtils';

interface DeviceCardProps {
  device: {
    deviceId: string;
    name: string;
    farmName: string;
    farmAssetId?: string;
    province?: string;
    isActive: boolean;
    telemetryHealthScore?: number;
    lastTelemetryCheck?: string;
    lastActivityTime?: string | Date;
    updatedAt?: string | Date;
    discoveryMethod?: string;
    sectionGroupsEnabled?: boolean;
    sectionGroups?: Record<string, string>;
    sprinklingSchedulerEnabled?: boolean;
    modelType?: string;
    modelVersion?: string;
    commissionedAt?: string;
    offlineSince?: string | Date;
    offlineDurationMs?: number;
    barnSprinklingCutoffTemperature?: number;
  };
  metrics?: {
    liquidFlowRate?: number;
    waterFlowRate?: number;
    dosingFlowRate?: number;
    mixtureSprayFlowRate?: number;
    liquidToMixtankFlowRate?: number;
    dosingEfficiency?: number;
    status: 'healthy' | 'warning' | 'critical' | 'offline';
    lastDosingTimestamp?: number;
  };
  barnTemperatures?: Array<{
    deviceId: string;
    deviceName: string;
    temperature: number;
    timestamp: string;
  }>;
}

export const DeviceCard: React.FC<DeviceCardProps> = ({ device, metrics, barnTemperatures }) => {
  const { thresholds } = useFlowThresholds();
  const { fetchFlowDegradationTimeline } = useDeviceContext();

  // State for flow degradation timeline (critical flow info)
  const [degradationTimeline, setDegradationTimeline] = useState<FlowDegradationTimeline | null>(null);
  const [degradationLoading, setDegradationLoading] = useState(false);
  const [degradationFailed, setDegradationFailed] = useState(false);

  // Get liquid flow thresholds from config (defaults: warning=15, critical=10)
  const liquidFlowThreshold = thresholds.find(t => t.thresholdName === 'liquid_input_flow');
  const warningThreshold = liquidFlowThreshold?.warningThreshold ?? 15;
  const criticalThreshold = liquidFlowThreshold?.criticalThreshold ?? 10;

  // Use liquidToMixtankFlowRate if available, otherwise fall back to dosingFlowRate
  // This ensures alerts match what anomaly detection uses (flowRateLpm from liquid dosing events)
  // liquidToMixtankFlowRate comes from mixtankDosingFinishedEvent with targetFluid=liquid
  // dosingFlowRate comes from liquidDosingFinishedEvent (same as anomaly detection source)
  const effectiveLiquidFlowRate = metrics?.liquidToMixtankFlowRate ?? metrics?.dosingFlowRate;

  // Capture current time once per render cycle via useMemo to satisfy React purity rules.
  // The dep on lastDosingTimestamp ensures it re-evaluates when telemetry changes.
  const isFlowStale = useMemo(() => {
    // Flow data is stale if last dosing event is older than 30 hours
    // (many farms dose once daily — 24h was tripping between normal daily cycles)
    return typeof metrics?.lastDosingTimestamp === 'number' &&
      metrics.lastDosingTimestamp > 0 &&
      (Date.now() - metrics.lastDosingTimestamp > FLOW_STALENESS_THRESHOLD_MS); // eslint-disable-line react-hooks/purity -- Date.now() needed for staleness check
  }, [metrics?.lastDosingTimestamp]);

  const _isCriticalFlow = effectiveLiquidFlowRate !== undefined && effectiveLiquidFlowRate <= criticalThreshold && !isFlowStale;

  // Fetch degradation timeline on user request (not auto-fetch to avoid rate limiting)
  const handleLoadDegradationTimeline = useCallback(() => {
    if (!degradationLoading && !degradationTimeline && !degradationFailed) {
      setDegradationLoading(true);
      setDegradationFailed(false);
      fetchFlowDegradationTimeline(device.deviceId)
        .then(timeline => {
          if (timeline) {
            setDegradationTimeline(timeline);
          } else {
            // null means fetch failed or service unavailable
            setDegradationFailed(true);
          }
        })
        .catch(() => {
          setDegradationFailed(true);
        })
        .finally(() => {
          setDegradationLoading(false);
        });
    }
  }, [device.deviceId, degradationLoading, degradationTimeline, degradationFailed, fetchFlowDegradationTimeline]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-100 text-green-800 border-green-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'offline': return 'bg-purple-100 text-purple-900 border-purple-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getHealthScoreColor = (score?: number) => {
    if (!score) return 'text-gray-500';
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatTimestamp = (timestampMs?: number) => {
    if (!timestampMs) return null;
    const date = new Date(timestampMs);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeAgo = (date?: string | Date) => {
    if (!date) return null;

    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMinutes < 1) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString();
  };

  const formatOfflineDuration = (durationMs: number): string => {
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `Offline for ${days} day${days > 1 ? 's' : ''} (${hours}h total)`;
    } else if (hours > 0) {
      return `Offline for ${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      const minutes = Math.floor(durationMs / (1000 * 60));
      return `Offline for ${minutes} minute${minutes > 1 ? 's' : ''}`;
    }
  };

  const getLastUpdateTime = () => {
    // Prefer lastActivityTime (actual device activity) over updatedAt (database update)
    return device.lastActivityTime || device.updatedAt || device.lastTelemetryCheck;
  };

  const getLastUpdateColor = (date?: string | Date) => {
    if (!date) return 'text-gray-400';
    
    const now = new Date();
    const then = new Date(date);
    const diffHours = (now.getTime() - then.getTime()) / (1000 * 60 * 60);
    
    if (diffHours < 2) return 'text-green-600'; // Fresh data
    if (diffHours < 24) return 'text-yellow-600'; // Somewhat old
    return 'text-red-600'; // Stale data
  };

  // Determine if device is uncommissioned (never set up)
  const isUncommissioned = !device.commissionedAt;

  // Determine card background based on status and sprinkling status
  // Green for healthy systems: sprinkling ON + no critical alerts + good flow
  const isHealthy = device.sprinklingSchedulerEnabled === true &&
                    metrics?.status !== 'offline' &&
                    !isFlowStale &&
                    effectiveLiquidFlowRate !== undefined &&
                    effectiveLiquidFlowRate >= warningThreshold &&
                    !(effectiveLiquidFlowRate <= criticalThreshold); // No critical flow issues

  // Priority: Uncommissioned > Offline > Sprinkling OFF > Healthy > Default
  const cardBgClass = isUncommissioned
    ? 'bg-gray-100 border-gray-400 border-2' // Uncommissioned: grey
    : metrics?.status === 'offline'
    ? 'bg-purple-50 border-purple-400 border-2' // Offline: purple
    : device.sprinklingSchedulerEnabled === false
    ? 'bg-orange-50 border-orange-300' // Sprinkling OFF: orange
    : isHealthy
    ? 'bg-green-50 border-green-200' // Healthy: subtle green
    : 'bg-white border-gray-200'; // Default: white

  return (
    <div className={`${cardBgClass} rounded-lg shadow-md border hover:shadow-lg transition-shadow`}>
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {shortDeviceName(device.name)}
            </h3>
            {device.province && (
              <p className="text-xs text-gray-500">{device.province}</p>
            )}
          </div>
          <div className="flex flex-col items-end space-y-1">
            {isUncommissioned && (
              <div className="px-2 py-1 rounded-full text-xs font-medium border bg-gray-200 text-gray-700 border-gray-400">
                Not Commissioned
              </div>
            )}
            <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(metrics?.status || 'offline')}`}>
              {metrics?.status || 'offline'}
            </div>
            {typeof device.sprinklingSchedulerEnabled === 'boolean' && (
              <div className={`px-2 py-1 rounded-full text-xs font-medium border ${
                device.sprinklingSchedulerEnabled
                  ? 'bg-green-100 text-green-800 border-green-300'
                  : 'bg-orange-100 text-orange-800 border-orange-300'
              }`}>
                💧 {device.sprinklingSchedulerEnabled ? 'Sprinkling ON' : 'Sprinkling OFF'}
                {/* Show cold weather indicator if ON but temp too cold */}
                {device.sprinklingSchedulerEnabled && barnTemperatures && typeof device.barnSprinklingCutoffTemperature === 'number' && device.barnSprinklingCutoffTemperature > 0 &&
                 barnTemperatures.some(s => s.temperature < device.barnSprinklingCutoffTemperature!) && (
                  <span className="ml-1">❄️</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* OFFLINE ALERT - Show when device has lost connectivity */}
        {metrics?.status === 'offline' && device.offlineDurationMs && (
          <div className={`mb-4 p-3 rounded-lg border-2 ${isUncommissioned ? 'bg-gray-100 border-gray-400' : 'bg-purple-100 border-purple-400'}`}>
            <div className="flex items-start space-x-2">
              <span className="text-2xl">{isUncommissioned ? '📦' : '🔌'}</span>
              <div className="flex-1">
                <p className={`text-sm font-bold ${isUncommissioned ? 'text-gray-800' : 'text-purple-900'}`}>
                  {isUncommissioned ? 'Not Yet Deployed' : 'OFFLINE: Device Not Responding'}
                </p>
                <p className={`text-xs mt-1 ${isUncommissioned ? 'text-gray-600' : 'text-purple-700'}`}>
                  {isUncommissioned
                    ? 'This device has not been commissioned. It is likely powered off or awaiting installation.'
                    : formatOfflineDuration(device.offlineDurationMs)}
                </p>
                {!isUncommissioned && (
                  <>
                    <p className="text-xs text-purple-600 mt-1">
                      Last seen: {device.offlineSince ? new Date(device.offlineSince).toLocaleString() : 'Unknown'}
                    </p>
                    <p className="text-xs text-purple-600 mt-2">
                      Check device power, network connection, and Remote.it status.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Barn Floor Temperature Section - Always show with placeholder when no data */}
        <div className="mb-4 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-sm font-medium text-gray-700">🌡️ Barn Temperature</span>
            {typeof device.barnSprinklingCutoffTemperature === 'number' && device.barnSprinklingCutoffTemperature > 0 && (
              <span className="text-xs text-gray-500">
                Min: {device.barnSprinklingCutoffTemperature}°C
              </span>
            )}
          </div>
          {barnTemperatures && barnTemperatures.length > 0 ? (
            <div className="space-y-1">
              {barnTemperatures.map((sensor) => {
                const isBelowCutoff = typeof device.barnSprinklingCutoffTemperature === 'number'
                  && device.barnSprinklingCutoffTemperature > 0
                  && sensor.temperature < device.barnSprinklingCutoffTemperature;

                const tempColor =
                  isBelowCutoff ? 'text-blue-600' :
                  sensor.temperature > 30 ? 'text-red-600' :
                  sensor.temperature < 5 ? 'text-blue-500' :
                  'text-gray-900';
                const bgColor =
                  isBelowCutoff ? 'bg-blue-100' :
                  sensor.temperature > 30 ? 'bg-red-50' :
                  sensor.temperature < 5 ? 'bg-blue-50' :
                  'bg-gray-50';

                return (
                  <div key={sensor.deviceId} className={`flex justify-between items-center px-2 py-1 rounded ${bgColor}`}>
                    <span className="text-xs text-gray-600">
                      {sensor.deviceName.replace(/\s*\[[a-f0-9]+\]/i, '')}
                      {isBelowCutoff && <span className="ml-1">❄️</span>}
                    </span>
                    <span className={`text-sm font-semibold ${tempColor}`}>
                      {sensor.temperature.toFixed(1)}°C
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex justify-between items-center px-2 py-1 rounded bg-gray-50">
              <span className="text-xs text-gray-500 italic">No thermometer data available</span>
              <span className="text-sm text-gray-400">--°C</span>
            </div>
          )}

          {/* Cold Weather Skip Alert */}
          {barnTemperatures && barnTemperatures.length > 0 && typeof device.barnSprinklingCutoffTemperature === 'number' && device.barnSprinklingCutoffTemperature > 0 && (
            barnTemperatures.some(s => s.temperature < device.barnSprinklingCutoffTemperature!) && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-300 rounded">
                <div className="flex items-center gap-1">
                  <span className="text-sm">❄️</span>
                  <span className="text-xs text-blue-700">
                    <strong>Cold Weather Protection:</strong> Dosing paused (temp below {device.barnSprinklingCutoffTemperature}°C)
                  </span>
                </div>
              </div>
            )
          )}
        </div>

        <div className="space-y-2 mb-4">
          {/* Flow Rates Section */}
          <div className="grid grid-cols-1 gap-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">🚿 Dosing Sprinkler:</span>
              <span className={`text-sm font-medium ${
                isFlowStale ? 'text-gray-400' :
                metrics?.mixtureSprayFlowRate === 0 && device.sprinklingSchedulerEnabled === true && metrics?.status !== 'offline' ? 'text-red-600' :
                'text-gray-900'
              }`}>
                {isFlowStale ? '-- L/min' :
                  metrics?.mixtureSprayFlowRate !== null && metrics?.mixtureSprayFlowRate !== undefined
                  ? `${metrics.mixtureSprayFlowRate.toFixed(1)} L/min`
                  : '-- L/min'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">🧪 Liquid Flow:</span>
              <span className={`text-sm font-medium ${
                isFlowStale ? 'text-gray-400' :
                effectiveLiquidFlowRate !== undefined && effectiveLiquidFlowRate <= criticalThreshold && device.sprinklingSchedulerEnabled === true && metrics?.status !== 'offline' ? 'text-red-600 font-bold' :
                effectiveLiquidFlowRate !== undefined && effectiveLiquidFlowRate < warningThreshold && device.sprinklingSchedulerEnabled === true && metrics?.status !== 'offline' ? 'text-amber-600 font-bold' :
                'text-gray-900'
              }`}>
                {isFlowStale ? '-- L/min' :
                  effectiveLiquidFlowRate !== null && effectiveLiquidFlowRate !== undefined
                  ? `${effectiveLiquidFlowRate.toFixed(1)} L/min`
                  : '-- L/min'}
                {!isFlowStale && effectiveLiquidFlowRate !== undefined && effectiveLiquidFlowRate <= criticalThreshold && device.sprinklingSchedulerEnabled === true && metrics?.status !== 'offline' && ' 🔴'}
                {!isFlowStale && effectiveLiquidFlowRate !== undefined && effectiveLiquidFlowRate > criticalThreshold && effectiveLiquidFlowRate < warningThreshold && device.sprinklingSchedulerEnabled === true && metrics?.status !== 'offline' && ' 🟡'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">💧 Water Flow:</span>
              <span className={`text-sm font-medium ${
                isFlowStale ? 'text-gray-400' :
                metrics?.waterFlowRate === 0 && device.sprinklingSchedulerEnabled === true && metrics?.status !== 'offline' ? 'text-red-600' :
                'text-gray-900'
              }`}>
                {isFlowStale ? '-- L/min' :
                  metrics?.waterFlowRate !== null && metrics?.waterFlowRate !== undefined
                  ? `${metrics.waterFlowRate.toFixed(1)} L/min`
                  : '-- L/min'}
              </span>
            </div>
            {/* Show legacy flow rate only if new fields are truly unavailable (undefined), not when they're zero */}
            {metrics?.mixtureSprayFlowRate === undefined &&
             metrics?.liquidToMixtankFlowRate === undefined &&
             metrics?.waterFlowRate === undefined &&
             typeof metrics?.liquidFlowRate === 'number' && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Flow Rate:</span>
                <span className="text-sm font-medium text-gray-900">
                  {metrics.liquidFlowRate.toFixed(1)} L/min
                </span>
              </div>
            )}
          </div>

          {/* Last Dosing Timestamp - especially important when sprinkling is OFF */}
          {/* Note: Use explicit boolean check to avoid rendering "0" (React renders 0 as text, and 0 && x still returns 0) */}
          {typeof metrics?.lastDosingTimestamp === 'number' && metrics.lastDosingTimestamp > 0 && (
            <div className={`mt-2 pt-2 border-t ${device.sprinklingSchedulerEnabled === false ? 'border-orange-200' : 'border-gray-200'}`}>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-500">📅 Last Dosing:</span>
                <span className={`font-medium ${device.sprinklingSchedulerEnabled === false ? 'text-orange-700' : 'text-gray-600'}`}>
                  {formatTimestamp(metrics.lastDosingTimestamp)}
                  {device.sprinklingSchedulerEnabled === false && ' (System OFF)'}
                </span>
              </div>
            </div>
          )}

          {/* CRITICAL ALERT: Low/Zero Liquid Flow (at or below critical threshold) */}
          {/* Only show when sprinkling is EXPLICITLY enabled (true) and data is fresh */}
          {/* Zero flow is expected when: 1) system is OFF, 2) device is offline, 3) status unknown (null), 4) data is stale */}
          {effectiveLiquidFlowRate !== undefined &&
           effectiveLiquidFlowRate <= criticalThreshold &&
           device.sprinklingSchedulerEnabled === true &&
           metrics?.status !== 'offline' &&
           !isFlowStale && (
            <div className="mt-3 p-3 bg-red-50 border-2 border-red-500 rounded-lg">
              <div className="flex items-start space-x-2">
                <span className="text-2xl">🚨</span>
                <div className="flex-1">
                  <p className="text-sm font-bold text-red-900">CRITICAL: MgCl Filter Blocked</p>
                  <p className="text-xs text-red-700 mt-1">
                    {effectiveLiquidFlowRate === 0
                      ? 'Zero liquid flow detected.'
                      : `Flow rate at ${effectiveLiquidFlowRate.toFixed(1)} L/min (at or below ${criticalThreshold} L/min critical threshold).`
                    } Filter requires immediate cleaning or replacement.
                    Contact service technician.
                  </p>
                  {/* Degradation Timeline Info */}
                  {!degradationTimeline && !degradationLoading && !degradationFailed && (
                    <button
                      onClick={handleLoadDegradationTimeline}
                      className="mt-2 text-xs text-red-700 underline hover:text-red-900 focus:outline-none"
                    >
                      📊 Show degradation history
                    </button>
                  )}
                  {degradationLoading && (
                    <p className="text-xs text-red-600 mt-2 italic">Loading degradation history...</p>
                  )}
                  {degradationFailed && !degradationLoading && (
                    <p className="text-xs text-red-500 mt-2 italic">
                      Degradation history unavailable
                    </p>
                  )}
                  {degradationTimeline && !degradationLoading && (
                    <div className="mt-2 pt-2 border-t border-red-300">
                      <p className="text-xs font-semibold text-red-800">📊 Degradation Timeline:</p>
                      <div className="mt-1 text-xs text-red-700 space-y-0.5">
                        {degradationTimeline.daysAtZero > 0 && (
                          <p>• <strong>{degradationTimeline.daysAtZero} day(s)</strong> at zero flow</p>
                        )}
                        {degradationTimeline.daysAtCritical > 0 && degradationTimeline.daysAtZero === 0 && (
                          <p>• <strong>{degradationTimeline.daysAtCritical} day(s)</strong> at critical level</p>
                        )}
                        {degradationTimeline.firstCriticalDate && (
                          <p>• Critical since: <strong>{new Date(degradationTimeline.firstCriticalDate).toLocaleDateString()}</strong></p>
                        )}
                        {degradationTimeline.daysFromHealthyToCritical !== null && (
                          <p>• Degraded in <strong>{degradationTimeline.daysFromHealthyToCritical} days</strong> from healthy to critical</p>
                        )}
                        {degradationTimeline.lastHealthyDate && (
                          <p>• Last healthy: {new Date(degradationTimeline.lastHealthyDate).toLocaleDateString()}</p>
                        )}
                        {degradationTimeline.degradationRate !== null && degradationTimeline.degradationRate > 0 && (
                          <p>• Degradation rate: {degradationTimeline.degradationRate.toFixed(1)} L/min per day</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* WARNING ALERT: Low Liquid Flow (below warning but above critical threshold) */}
          {/* Only show when sprinkling is EXPLICITLY enabled (true) and data is fresh */}
          {effectiveLiquidFlowRate !== undefined &&
           effectiveLiquidFlowRate > criticalThreshold &&
           effectiveLiquidFlowRate < warningThreshold &&
           device.sprinklingSchedulerEnabled === true &&
           metrics?.status !== 'offline' &&
           !isFlowStale && (
            <div className="mt-3 p-3 bg-amber-50 border-2 border-amber-500 rounded-lg">
              <div className="flex items-start space-x-2">
                <span className="text-2xl">⚠️</span>
                <div className="flex-1">
                  <p className="text-sm font-bold text-amber-900">WARNING: Low MgCl Flow Rate</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Flow rate at {effectiveLiquidFlowRate.toFixed(1)} L/min (below {warningThreshold} L/min threshold).
                    Filter may need cleaning soon.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Dosing Efficiency - only show when sprinkling is enabled */}
          {device.sprinklingSchedulerEnabled !== false && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Dosing Efficiency:</span>
              <span className="text-sm font-medium text-gray-900">
                {metrics?.dosingEfficiency?.toFixed(0) || '--'}%
              </span>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center text-xs text-gray-500 mb-2">
          <span>Health Score:</span>
          <span className={`font-medium ${getHealthScoreColor(device.telemetryHealthScore)}`}>
            {device.telemetryHealthScore !== null && device.telemetryHealthScore !== undefined ? device.telemetryHealthScore : '--'}%
          </span>
        </div>

        <div className="flex justify-between items-center text-xs mb-4">
          <span className="text-gray-500">Last Updated:</span>
          <span className={`font-medium ${getLastUpdateColor(getLastUpdateTime())}`}>
            {getTimeAgo(getLastUpdateTime()) || 'Unknown'}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${device.isActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
            <span className="text-xs text-gray-500">
              {device.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="flex items-center space-x-3 flex-shrink-0">
            {device.farmAssetId && (
              <Link
                href={`/assets/${device.farmAssetId}`}
                className="text-struvital-800 hover:text-struvital-900 text-sm font-medium whitespace-nowrap"
              >
                Farm
              </Link>
            )}
            <Link
              href={`/device/${device.deviceId}`}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium whitespace-nowrap"
            >
              Details →
            </Link>
          </div>
        </div>

        {device.sectionGroups && Object.keys(device.sectionGroups).length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
            <span className="text-xs text-gray-500">
              {Object.keys(device.sectionGroups).length} sections
              {new Set(Object.values(device.sectionGroups)).size > 1
                ? ` · ${new Set(Object.values(device.sectionGroups)).size} groups`
                : ''}:
            </span>
            <div className="flex gap-1 flex-wrap">
              {Object.entries(device.sectionGroups)
                .sort(([a], [b]) => parseInt(a.replace(/\D/g, '')) - parseInt(b.replace(/\D/g, '')))
                .map(([key, group]) => (
                  <span
                    key={key}
                    className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                      group === '1' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'
                    }`}
                  >
                    {key.replace('groupS', 'S')}
                  </span>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};